import React, { useState, useRef, useEffect, useCallback } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const CARD_W = 856;
const CARD_H  = 540;
const ADMIN_PASSWORD = "cnic2024admin"; // ← change this password
const CONFIG_URL     = "/admin-config.json"; // served as static file from public/

// ─── Default Fields ───────────────────────────────────────────────────────────
const FRONT_FIELDS = [
  { id:"name",           label:"Name (English)",        type:"text",      x:215,y:110,w:310,h:26, fontSize:16,color:"#000000",fontWeight:"bold",   rtl:false },
  { id:"fatherName",     label:"Father Name (English)", type:"text",      x:215,y:178,w:310,h:24, fontSize:15,color:"#000000",fontWeight:"bold",   rtl:false },
  { id:"gender",         label:"Gender",                type:"text",      x:215,y:228,w:70, h:20, fontSize:13,color:"#000000",fontWeight:"normal", rtl:false },
  { id:"country",        label:"Country of Stay",       type:"text",      x:310,y:242,w:200,h:22, fontSize:13,color:"#000000",fontWeight:"bold",   rtl:false },
  { id:"idNumber",       label:"Identity Number",       type:"text",      x:215,y:282,w:210,h:22, fontSize:13,color:"#000000",fontWeight:"normal", rtl:false },
  { id:"dob",            label:"Date of Birth",         type:"text",      x:448,y:282,w:170,h:22, fontSize:13,color:"#000000",fontWeight:"normal", rtl:false },
  { id:"issueDate",      label:"Date of Issue",         type:"text",      x:215,y:318,w:180,h:20, fontSize:12,color:"#000000",fontWeight:"normal", rtl:false },
  { id:"expiryDate",     label:"Date of Expiry",        type:"text",      x:448,y:318,w:180,h:20, fontSize:12,color:"#000000",fontWeight:"normal", rtl:false },
  { id:"nameUrdu",       label:"Name (Urdu)",           type:"text",      x:530,y:110,w:200,h:26, fontSize:16,color:"#000000",fontWeight:"bold",   rtl:true  },
  { id:"fatherNameUrdu", label:"Father Name (Urdu)",    type:"text",      x:530,y:178,w:200,h:24, fontSize:15,color:"#000000",fontWeight:"normal", rtl:true  },
  { id:"watermarkNum",   label:"Watermark Number",      type:"watermark", x:30, y:240,w:160,h:60, fontSize:32,color:"#000000",fontWeight:"bold",   rtl:false, opacity:0.18 },
  { id:"circleWatermark",label:"Circle Watermark",      type:"circle",    x:490,y:80, w:100,h:100,fontSize:12,color:"#000000",fontWeight:"normal", rtl:false, opacity:0.15 },
  { id:"photo",          label:"Main Photo",            type:"image",     x:570,y:100,w:148,h:195,fontSize:12,color:"#000000",fontWeight:"normal", rtl:false, removeBg:true,  opacity:1.0 },
  { id:"miniPhoto",      label:"Mini Photo",            type:"image",     x:32, y:370,w:70, h:90, fontSize:12,color:"#000000",fontWeight:"normal", rtl:false, removeBg:true,  opacity:1.0 },
  { id:"signature",      label:"Signature",             type:"image",     x:560,y:330,w:180,h:60, fontSize:12,color:"#000000",fontWeight:"normal", rtl:false, removeBg:false, opacity:1.0 },
];
const BACK_FIELDS = [
  { id:"tempAddressUrdu", label:"Temporary Address (Urdu)", type:"text",  x:80, y:60, w:480,h:28, fontSize:14,color:"#000000",fontWeight:"normal",rtl:true  },
  { id:"permAddressUrdu", label:"Permanent Address (Urdu)", type:"text",  x:80, y:150,w:480,h:28, fontSize:14,color:"#000000",fontWeight:"normal",rtl:true  },
  { id:"familyCode",      label:"Family Code",              type:"text",  x:580,y:430,w:200,h:24, fontSize:13,color:"#000000",fontWeight:"bold",  rtl:false },
  { id:"cnicAboveQr",     label:"CNIC Number (above QR)",   type:"text",  x:560,y:60, w:240,h:22, fontSize:12,color:"#000000",fontWeight:"normal",rtl:false },
  { id:"backMiniPhoto",   label:"Mini Photo (back)",         type:"image", x:30, y:60, w:70, h:90, fontSize:12,color:"#000000",fontWeight:"normal",rtl:false, removeBg:true, opacity:1.0 },
  { id:"qrCode",          label:"QR Code",                  type:"qr",    x:570,y:85, w:160,h:160,fontSize:12,color:"#000000",fontWeight:"normal",rtl:false, opacity:1.0 },
];
const DEFAULT_FRONT = { id:"front", name:"CNIC Front", bgColor:"#c8e6c9", bgImage:null, cardW:CARD_W, cardH:CARD_H, fields:FRONT_FIELDS };
const DEFAULT_BACK  = { id:"back",  name:"CNIC Back",  bgColor:"#c8e6c9", bgImage:null, cardW:CARD_W, cardH:CARD_H, fields:BACK_FIELDS  };

const FIELD_COLORS = {
  name:"#059669",fatherName:"#0891b2",gender:"#d97706",country:"#7c3aed",
  idNumber:"#dc2626",dob:"#be185d",issueDate:"#065f46",expiryDate:"#92400e",
  nameUrdu:"#6366f1",fatherNameUrdu:"#0e7490",watermarkNum:"#78716c",
  circleWatermark:"#1e293b",photo:"#4f46e5",miniPhoto:"#7c3aed",signature:"#b45309",
  tempAddressUrdu:"#0f766e",permAddressUrdu:"#1d4ed8",familyCode:"#9333ea",
  cnicAboveQr:"#dc2626",backMiniPhoto:"#7c3aed",qrCode:"#064e3b",
};
const FIELD_LABELS = {
  name:"Name (English)",fatherName:"Father Name (English)",gender:"Gender (M/F)",
  country:"Country of Stay",idNumber:"Identity Number",dob:"Date of Birth",
  issueDate:"Date of Issue",expiryDate:"Date of Expiry",
  nameUrdu:"Name (Urdu)",fatherNameUrdu:"Father Name (Urdu)",
  watermarkNum:"Watermark Number",circleWatermark:"Circle Watermark",
  photo:"Main Photo",miniPhoto:"Mini Photo (front)",signature:"Signature",
  tempAddressUrdu:"Temporary Address (Urdu)",permAddressUrdu:"Permanent Address (Urdu)",
  familyCode:"Family Code",cnicAboveQr:"CNIC Number (above QR)",
  backMiniPhoto:"Mini Photo (back)",qrCode:"QR Code",
};

// ─── Utilities ────────────────────────────────────────────────────────────────
function toDataURL(file){
  return new Promise(res=>{ const r=new FileReader(); r.onload=e=>res(e.target.result); r.readAsDataURL(file); });
}
function loadImg(src){
  return new Promise((res,rej)=>{ const i=new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=src; });
}

// ─── Jameel Noori Nastaleeq font loader ───────────────────────────────────────
let _urduFontLoaded=false;
let _urduFontPromise=null;
function ensureUrduFont(){
  if(_urduFontLoaded) return Promise.resolve();
  if(_urduFontPromise) return _urduFontPromise;
  _urduFontPromise=(async()=>{
    try{
      if(!document.getElementById("jameel-noori-font")){
        const link=document.createElement("link");
        link.id="jameel-noori-font";
        link.rel="stylesheet";
        link.href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap";
        document.head.appendChild(link);
      }
      // Race font load against 3s timeout — never block canvas forever
      await Promise.race([
        document.fonts.load('16px "Noto Nastaliq Urdu"'),
        new Promise(r=>setTimeout(r,3000))
      ]);
    }catch(_){}
    _urduFontLoaded=true;
  })();
  return _urduFontPromise;
}
// Pre-load font immediately on module init (non-blocking)
ensureUrduFont();
const URDU_FONT='"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", serif';

// Load admin config from server (shared by all users)
async function fetchServerConfig(){
  try{
    const r=await fetch(CONFIG_URL+"?t="+Date.now()); // bust cache
    if(!r.ok) return null;
    return await r.json();
  }catch(_){ return null; }
}

// ─── QR Code ─────────────────────────────────────────────────────────────────
async function generateQRDataURL(text,size){
  if(!text) return null;
  if(!window.QRCode){
    await new Promise((res,rej)=>{ const s=document.createElement("script"); s.src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"; s.onload=res; s.onerror=rej; document.head.appendChild(s); });
  }
  return new Promise(res=>{
    const div=document.createElement("div"); document.body.appendChild(div);
    new window.QRCode(div,{text,width:size,height:size,colorDark:"#000000",colorLight:"#ffffff",correctLevel:window.QRCode.CorrectLevel.M});
    setTimeout(()=>{
      const img=div.querySelector("img")||div.querySelector("canvas");
      let url=null;
      if(img&&img.tagName==="IMG") url=img.src;
      else if(img&&img.tagName==="CANVAS") url=img.toDataURL();
      document.body.removeChild(div); res(url);
    },300);
  });
}

// ─── Draw Card ────────────────────────────────────────────────────────────────
async function drawCard(canvas,tpl,formData,uploads,scale){
  const W=(tpl.cardW||CARD_W)*scale, H=(tpl.cardH||CARD_H)*scale;
  canvas.width=W; canvas.height=H;
  const ctx=canvas.getContext("2d");
  ctx.clearRect(0,0,W,H);
  if(tpl.bgImage){
    try{ const img=await loadImg(tpl.bgImage); ctx.drawImage(img,0,0,W,H); }
    catch(_){ ctx.fillStyle=tpl.bgColor||"#fff"; ctx.fillRect(0,0,W,H); }
  } else { ctx.fillStyle=tpl.bgColor||"#fff"; ctx.fillRect(0,0,W,H); }

  // ── Layer order: 1) circle & watermark (behind everything)
  //                2) images (photos, QR)
  //                3) text (on top)
  const LAYER_ORDER=["image","qr","circle","watermark","text"];
  const sortedFields=[...tpl.fields].sort((a,b)=>LAYER_ORDER.indexOf(a.type)-LAYER_ORDER.indexOf(b.type));

  for(const f of sortedFields){
    const x=f.x*scale,y=f.y*scale,w=f.w*scale,h=f.h*scale;
    const alpha=f.opacity!==undefined?f.opacity:1.0;
    if(f.type==="circle"){
      ctx.save(); ctx.globalAlpha=alpha;
      ctx.beginPath(); ctx.arc(x+w/2,y+h/2,Math.min(w,h)/2,0,Math.PI*2);
      ctx.fillStyle=f.color||"#000"; ctx.fill(); ctx.restore(); continue;
    }
    if(f.type==="watermark"){
      const val=(formData?.[f.id]||"").trim(); if(!val) continue;
      ctx.save(); ctx.globalAlpha=alpha;
      ctx.font=`bold ${f.fontSize*scale}px Arial,sans-serif`;
      ctx.fillStyle=f.color||"#000"; ctx.textAlign="left"; ctx.textBaseline="middle";
      ctx.fillText(val,x,y+h/2); ctx.restore(); continue;
    }
    if(f.type==="qr"){
      const qrText=formData?.qrData||(formData?.idNumber||""); if(!qrText) continue;
      try{
        const qrURL=await generateQRDataURL(qrText,Math.round(w));
        if(qrURL){ const img=await loadImg(qrURL); ctx.save(); ctx.globalAlpha=alpha; ctx.drawImage(img,x,y,w,h); ctx.restore(); }
      }catch(_){} continue;
    }
    if(f.type==="image"){
      const src=uploads?.[f.id]; if(!src) continue;
      try{
        const img=await loadImg(src);
        ctx.save(); ctx.globalAlpha=alpha;
        ctx.beginPath(); ctx.rect(x,y,w,h); ctx.clip();
        const ar=img.width/img.height; const dh=h,dw=dh*ar;
        ctx.drawImage(img,x+(w-dw)/2,y,dw,dh);
        ctx.restore();
        // NO BORDER — images blend cleanly with card
      }catch(_){} continue;
    }
    if(f.type==="text"){
      const val=(formData?.[f.id]||"").trim(); if(!val) continue;
      ctx.save(); ctx.globalAlpha=alpha;
      let fs=f.fontSize*scale;
      const fw=f.fontWeight==="bold"?"bold ":"";
      const urduFont=()=>`${fw}${fs}px ${URDU_FONT}`;
      const latinFont=()=>`${fw}${fs}px Arial,sans-serif`;
      // A word is "numeric/latin" if it contains only digits, dots, dashes, slashes, or latin chars
      const isNumericWord=w=>(/^[0-9\-./,:;#&@()A-Za-z]+$/).test(w);
      ctx.fillStyle=f.color||"#000"; ctx.textBaseline="middle";
      if(f.rtl){
        // Build lines first using Urdu font for width measurement
        ctx.font=urduFont();
        ctx.textAlign="right";
        const words=val.split(" ");
        // Group words into lines
        const lines=[]; let line=[];
        for(const word of words){
          const testLine=[...line,word].join(" ");
          ctx.font=urduFont();
          if(ctx.measureText(testLine).width>w-4*scale&&line.length>0){
            lines.push(line); line=[word];
          } else { line.push(word); }
        }
        if(line.length>0) lines.push(line);
        const lineH=fs*1.6;
        let ly=y+lineH/2;
        for(const lineWords of lines){
          // Draw each word individually with correct font, RTL (right to left)
          // First measure total line width with mixed fonts
          let totalW=0;
          const wordWidths=lineWords.map(word=>{
            ctx.font=isNumericWord(word)?latinFont():urduFont();
            const ww=ctx.measureText(word).width;
            return ww;
          });
          const spaceW=4*scale;
          totalW=wordWidths.reduce((a,b)=>a+b,0)+spaceW*(lineWords.length-1);
          // Draw right-to-left: start from x+w, move left
          let cx=x+w;
          for(let i=0;i<lineWords.length;i++){
            const word=lineWords[i];
            const numeric=isNumericWord(word);
            ctx.font=numeric?latinFont():urduFont();
            ctx.textAlign="right";
            ctx.fillText(word,cx,ly);
            cx-=(wordWidths[i]+spaceW);
          }
          ly+=lineH;
        }
      } else {
        ctx.font=latinFont();
        ctx.textAlign="left";
        while(fs>6*scale&&ctx.measureText(val).width>w-2*scale){fs-=0.5;ctx.font=latinFont();}
        let txt=val; while(ctx.measureText(txt).width>w-2*scale&&txt.length>1)txt=txt.slice(0,-1); if(txt!==val)txt+="\u2026";
        ctx.fillText(txt,x,y+h/2);
      }
      ctx.restore();
    }
  }
}

// ─── Photo Uploader ───────────────────────────────────────────────────────────
function PhotoUploader({fieldId,label,onUploaded}){
  const [preview,setPreview]=useState(null);
  async function handleFile(file){
    if(!file) return;
    const url=await toDataURL(file);
    setPreview(url); onUploaded(fieldId,url);
  }
  return (
    <div style={{marginBottom:12}}>
      <span style={{fontSize:11,color:"rgba(255,255,255,0.45)",display:"block",marginBottom:5}}>{label}</span>
      <label style={{display:"block",border:"2px dashed rgba(99,102,241,0.4)",borderRadius:8,cursor:"pointer",background:"rgba(99,102,241,0.06)",textAlign:"center",padding:preview?"0":"12px 8px",overflow:"hidden"}}>
        {preview
          ?<img src={preview} alt="" style={{width:"100%",display:"block",maxHeight:110,objectFit:"contain",background:"repeating-conic-gradient(#33333355 0% 25%,transparent 0% 50%) 0 0/14px 14px"}} />
          :<span style={{color:"#a5b4fc",fontSize:12}}>📁 Upload {label} (PNG without background)</span>}
        <input type="file" accept="image/*" onChange={e=>handleFile(e.target.files[0])} style={{display:"none"}} />
      </label>
      {preview&&<button onClick={()=>{setPreview(null);onUploaded(fieldId,null);}} style={{marginTop:4,width:"100%",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:6,color:"#f87171",cursor:"pointer",padding:"3px",fontSize:10}}>Remove</button>}
    </div>
  );
}

// ─── Field Panel (admin fine-tune) ────────────────────────────────────────────
function FieldPanel({field,onChange,onDelete}){
  const c=FIELD_COLORS[field.id]||"#a78bfa";
  const hasOpacity=["watermark","circle","image","qr"].includes(field.type);
  return (
    <div style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${c}55`,borderRadius:8,padding:"9px 11px",marginBottom:7}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
        <span style={{fontSize:11,fontWeight:700,color:c}}>{FIELD_LABELS[field.id]||field.label}</span>
        <button onClick={onDelete} style={{background:"rgba(239,68,68,0.2)",border:"none",borderRadius:4,color:"#f87171",cursor:"pointer",padding:"1px 6px",fontSize:10}}>✕</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:5}}>
        {["x","y","w","h"].map(k=>(
          <label key={k} style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>{k.toUpperCase()}
            <input type="number" value={field[k]} onChange={e=>onChange({...field,[k]:+e.target.value})}
              style={{display:"block",width:"100%",background:"rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:4,color:"#fff",padding:"3px 5px",fontSize:10,marginTop:2,boxSizing:"border-box"}} />
          </label>
        ))}
      </div>
      {field.type==="text"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:5,marginTop:6}}>
          <label style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>Size
            <input type="number" value={field.fontSize} onChange={e=>onChange({...field,fontSize:+e.target.value})}
              style={{display:"block",width:"100%",background:"rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:4,color:"#fff",padding:"3px 5px",fontSize:10,marginTop:2,boxSizing:"border-box"}} />
          </label>
          <label style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>Color
            <input type="color" value={field.color} onChange={e=>onChange({...field,color:e.target.value})}
              style={{display:"block",width:"100%",height:24,marginTop:2,border:"none",background:"transparent",cursor:"pointer"}} />
          </label>
          <label style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>Weight
            <select value={field.fontWeight} onChange={e=>onChange({...field,fontWeight:e.target.value})}
              style={{display:"block",width:"100%",background:"rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:4,color:"#fff",padding:"3px",fontSize:10,marginTop:2}}>
              <option value="normal">Normal</option><option value="bold">Bold</option>
            </select>
          </label>
        </div>
      )}
      {hasOpacity&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,marginTop:6}}>
          {(field.type==="watermark"||field.type==="circle")&&(
            <label style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>Color
              <input type="color" value={field.color} onChange={e=>onChange({...field,color:e.target.value})}
                style={{display:"block",width:"100%",height:24,marginTop:2,border:"none",background:"transparent",cursor:"pointer"}} />
            </label>
          )}
          {field.type==="watermark"&&(
            <label style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>Font Size
              <input type="number" value={field.fontSize} onChange={e=>onChange({...field,fontSize:+e.target.value})}
                style={{display:"block",width:"100%",background:"rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:4,color:"#fff",padding:"3px 5px",fontSize:10,marginTop:2,boxSizing:"border-box"}} />
            </label>
          )}
          <label style={{fontSize:10,color:"rgba(255,255,255,0.35)",gridColumn:(field.type==="image"||field.type==="qr")?"span 2":"auto"}}>
            Opacity {Math.round((field.opacity??1)*100)}%
            <input type="range" min="1" max="100" value={Math.round((field.opacity??1)*100)}
              onChange={e=>onChange({...field,opacity:+e.target.value/100})}
              style={{display:"block",width:"100%",marginTop:4,accentColor:c}} />
          </label>
        </div>
      )}
    </div>
  );
}

// ─── Setup Mode (admin template editor) ───────────────────────────────────────
function SetupMode({template,onSave}){
  const canvasRef=useRef(null);
  const [fields,  setFields]  = useState(()=>template.fields.map(f=>({...f})));
  const [bgImage, setBgImage] = useState(template.bgImage||null);
  const [bgColor, setBgColor] = useState(template.bgColor||"#c8e6c9");
  const [tplName, setTplName] = useState(template.name);
  const [placing, setPlacing] = useState(null);
  const [status,  setStatus]  = useState("");
  const cW=template.cardW||CARD_W, cH=template.cardH||CARD_H;

  // Redraw canvas when fields/bg change
  useEffect(()=>{
    const canvas=canvasRef.current; if(!canvas) return;
    let cancelled=false;

    // Set size ONCE — never reset inside onload (resizing clears canvas to black)
    canvas.width=cW; canvas.height=cH;
    const ctx=canvas.getContext("2d");

    const drawFields=()=>{
      if(cancelled) return;
      fields.forEach(f=>{
        const c=FIELD_COLORS[f.id]||"#a78bfa";
        if(f.type==="circle"){
          ctx.save(); ctx.globalAlpha=f.opacity??0.15;
          ctx.beginPath(); ctx.arc(f.x+f.w/2,f.y+f.h/2,Math.min(f.w,f.h)/2,0,Math.PI*2);
          ctx.fillStyle=f.color||"#000"; ctx.fill(); ctx.restore();
          ctx.strokeStyle=c; ctx.lineWidth=1.5; ctx.setLineDash([4,3]);
          ctx.beginPath(); ctx.arc(f.x+f.w/2,f.y+f.h/2,Math.min(f.w,f.h)/2,0,Math.PI*2); ctx.stroke();
          ctx.setLineDash([]);
        } else if(f.type==="qr"){
          ctx.fillStyle=c+"28"; ctx.fillRect(f.x,f.y,f.w,f.h);
          ctx.strokeStyle=c; ctx.lineWidth=1.5; ctx.setLineDash([4,3]);
          ctx.strokeRect(f.x,f.y,f.w,f.h); ctx.setLineDash([]);
          ctx.fillStyle=c; ctx.font="bold 10px Arial"; ctx.textAlign="left";
          ctx.fillText("QR Code",f.x+3,f.y+13);
        } else {
          ctx.fillStyle=c+"28"; ctx.fillRect(f.x,f.y,f.w,f.h);
          ctx.strokeStyle=c; ctx.lineWidth=1.5; ctx.setLineDash([4,3]);
          ctx.strokeRect(f.x,f.y,f.w,f.h); ctx.setLineDash([]);
          ctx.fillStyle=c;
          if(f.rtl){
            ctx.font=`bold 10px ${URDU_FONT}`; ctx.textAlign="right";
            ctx.fillText(FIELD_LABELS[f.id]||f.label,f.x+f.w-3,f.y+13);
          } else {
            ctx.font="bold 10px Arial"; ctx.textAlign="left";
            ctx.fillText(FIELD_LABELS[f.id]||f.label,f.x+3,f.y+13);
          }
        }
      });
      if(placing){
        ctx.fillStyle="rgba(255,230,0,0.9)"; ctx.font="bold 13px Arial"; ctx.textAlign="left";
        ctx.fillText(`Click to place: ${FIELD_LABELS[placing]||placing}`,8,cH-10);
      }
    };

    if(bgImage){
      // Draw fallback color first — canvas is never black while image loads
      ctx.fillStyle=bgColor; ctx.fillRect(0,0,cW,cH);
      const img=new Image();
      // Never use crossOrigin on base64 data URLs — causes silent failure
      if(!bgImage.startsWith("data:")) img.crossOrigin="anonymous";
      img.onload=()=>{
        if(cancelled) return; // newer effect ran, discard
        // clearRect only — do NOT reset canvas.width/height (clears to black)
        ctx.clearRect(0,0,cW,cH);
        ctx.drawImage(img,0,0,cW,cH);
        drawFields();
      };
      img.onerror=()=>{
        if(cancelled) return;
        ctx.fillStyle=bgColor; ctx.fillRect(0,0,cW,cH);
        drawFields();
      };
      img.src=bgImage;
    } else {
      ctx.fillStyle=bgColor; ctx.fillRect(0,0,cW,cH);
      drawFields();
    }

    return ()=>{ cancelled=true; };
  },[fields,bgImage,bgColor,placing,cW,cH]);

  function getPos(e){
    const r=canvasRef.current.getBoundingClientRect();
    return {x:Math.round((e.clientX-r.left)*(cW/r.width)),y:Math.round((e.clientY-r.top)*(cH/r.height))};
  }
  const DEFS={
    photo:{type:"image",w:148,h:195,removeBg:true,opacity:1.0},miniPhoto:{type:"image",w:70,h:90,removeBg:true,opacity:1.0},
    backMiniPhoto:{type:"image",w:70,h:90,removeBg:true,opacity:1.0},signature:{type:"image",w:180,h:60,removeBg:false,opacity:1.0},
    circleWatermark:{type:"circle",w:100,h:100,opacity:0.15,color:"#000000"},
    watermarkNum:{type:"watermark",w:160,h:60,fontSize:32,fontWeight:"bold",opacity:0.18,color:"#000000"},
    qrCode:{type:"qr",w:160,h:160,opacity:1.0},
    name:{type:"text",w:310,h:26,fontSize:16,fontWeight:"bold",rtl:false},nameUrdu:{type:"text",w:200,h:26,fontSize:16,fontWeight:"bold",rtl:true},
    fatherName:{type:"text",w:310,h:24,fontSize:15,fontWeight:"bold",rtl:false},fatherNameUrdu:{type:"text",w:200,h:24,fontSize:15,fontWeight:"normal",rtl:true},
    gender:{type:"text",w:70,h:20,fontSize:13,fontWeight:"normal",rtl:false},country:{type:"text",w:200,h:22,fontSize:13,fontWeight:"bold",rtl:false},
    idNumber:{type:"text",w:210,h:22,fontSize:13,fontWeight:"normal",rtl:false},dob:{type:"text",w:170,h:22,fontSize:13,fontWeight:"normal",rtl:false},
    issueDate:{type:"text",w:180,h:20,fontSize:12,fontWeight:"normal",rtl:false},expiryDate:{type:"text",w:180,h:20,fontSize:12,fontWeight:"normal",rtl:false},
    tempAddressUrdu:{type:"text",w:480,h:28,fontSize:14,fontWeight:"normal",rtl:true},permAddressUrdu:{type:"text",w:480,h:28,fontSize:14,fontWeight:"normal",rtl:true},
    familyCode:{type:"text",w:200,h:24,fontSize:13,fontWeight:"bold",rtl:false},cnicAboveQr:{type:"text",w:240,h:22,fontSize:12,fontWeight:"normal",rtl:false},
  };
  function handleClick(e){
    if(!placing) return;
    const {x,y}=getPos(e);
    const d=DEFS[placing]||{type:"text",w:200,h:24,fontSize:14,fontWeight:"normal",rtl:false};
    setFields(p=>[...p.filter(f=>f.id!==placing),{id:placing,label:FIELD_LABELS[placing]||placing,color:"#000000",x,y,...d}]);
    setPlacing(null); setStatus(`✓ ${FIELD_LABELS[placing]} placed`);
    setTimeout(()=>setStatus(""),2000);
  }
  const isFront=template.id==="front";
  const GROUPS=isFront?[
    {g:"📷 Photos & Signature",items:["photo","miniPhoto","signature"]},
    {g:"⚫ Watermarks",items:["circleWatermark","watermarkNum"]},
    {g:"🇬🇧 English Fields",items:["name","fatherName","gender","country","idNumber","dob","issueDate","expiryDate"]},
    {g:"🇵🇰 Urdu Fields",items:["nameUrdu","fatherNameUrdu"]},
  ]:[
    {g:"📷 Mini Photo",items:["backMiniPhoto"]},
    {g:"📱 QR Code",items:["qrCode"]},
    {g:"🇵🇰 Urdu Address",items:["tempAddressUrdu","permAddressUrdu"]},
    {g:"🔢 Other",items:["cnicAboveQr","familyCode"]},
  ];

  function handleSave(){
    onSave({...template,name:tplName,fields,bgImage,bgColor});
    setStatus("✓ Template saved!"); setTimeout(()=>setStatus(""),2500);
  }

  return (
    <div style={{display:"flex",gap:16,alignItems:"flex-start",flexWrap:"wrap"}}>
      <div style={{flex:"1 1 500px"}}>
        <div style={{background:"#111",borderRadius:10,padding:8,border:`2px dashed ${placing?"#fbbf24":"rgba(255,255,255,0.1)"}`}}>
          <canvas ref={canvasRef} onClick={handleClick} style={{width:"100%",display:"block",borderRadius:6,cursor:placing?"crosshair":"default"}} />
        </div>
        {placing&&<div style={{marginTop:6,background:"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.35)",borderRadius:6,padding:"6px 12px",color:"#fcd34d",fontSize:12,textAlign:"center"}}>
          Click on the card to place: <strong>{FIELD_LABELS[placing]||placing}</strong>
        </div>}
        {status&&<div style={{marginTop:6,background:"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.3)",borderRadius:6,padding:"6px 12px",color:"#6ee7b7",fontSize:12,textAlign:"center"}}>{status}</div>}
      </div>
      <div style={{flex:"0 0 272px",minWidth:230}}>
        <label style={{fontSize:11,color:"rgba(255,255,255,0.4)",display:"block",marginBottom:10}}>Template Name
          <input value={tplName} onChange={e=>setTplName(e.target.value)}
            style={{display:"block",width:"100%",background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.14)",borderRadius:6,color:"#fff",padding:"7px 10px",fontSize:13,marginTop:3,boxSizing:"border-box"}} />
        </label>
        <div style={{background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"10px 12px",marginBottom:11}}>
          <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.35)",marginBottom:7,textTransform:"uppercase",letterSpacing:1}}>Background</div>
          <label style={{display:"block",background:"rgba(99,102,241,0.14)",border:"1px solid rgba(99,102,241,0.3)",borderRadius:6,padding:"8px 10px",textAlign:"center",cursor:"pointer",color:"#a5b4fc",fontSize:12,marginBottom:6}}>
            {bgImage?"✓ Image loaded — click to replace":"📁 Upload background image"}
            <input type="file" accept="image/*" onChange={async e=>{const f=e.target.files[0];if(f)setBgImage(await toDataURL(f));}} style={{display:"none"}} />
          </label>
          {bgImage&&<button onClick={()=>setBgImage(null)} style={{width:"100%",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:6,color:"#f87171",cursor:"pointer",padding:"4px",fontSize:11,marginBottom:6}}>Remove image</button>}
          {!bgImage&&<div style={{display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>Color:</span>
            <input type="color" value={bgColor} onChange={e=>setBgColor(e.target.value)} style={{height:24,border:"none",background:"transparent",cursor:"pointer"}} />
          </div>}
        </div>
        <div style={{background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"10px 12px",marginBottom:11}}>
          <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.35)",marginBottom:7,textTransform:"uppercase",letterSpacing:1}}>Place Field on Card</div>
          {GROUPS.map(g=>(
            <div key={g.g} style={{marginBottom:8}}>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginBottom:4}}>{g.g}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                {g.items.map(ft=>(
                  <button key={ft} onClick={()=>setPlacing(placing===ft?null:ft)}
                    style={{padding:"3px 8px",borderRadius:4,fontSize:10,cursor:"pointer",background:placing===ft?(FIELD_COLORS[ft]||"#7c3aed"):"rgba(255,255,255,0.07)",border:`1px solid ${placing===ft?"transparent":"rgba(255,255,255,0.1)"}`,color:placing===ft?"#fff":"rgba(255,255,255,0.6)",fontWeight:placing===ft?700:400}}>
                    {ft}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.35)",marginBottom:7,textTransform:"uppercase",letterSpacing:1}}>Fine-tune Fields</div>
        <div style={{maxHeight:280,overflowY:"auto"}}>
          {fields.map(f=>(
            <FieldPanel key={f.id} field={f}
              onChange={u=>setFields(p=>p.map(x=>x.id===u.id?u:x))}
              onDelete={()=>setFields(p=>p.filter(x=>x.id!==f.id))} />
          ))}
        </div>
        <button onClick={handleSave}
          style={{width:"100%",marginTop:10,padding:"11px",borderRadius:8,cursor:"pointer",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",border:"none",color:"#fff",fontWeight:700,fontSize:14}}>
          💾 Save This Template
        </button>
      </div>
    </div>
  );
}

// ─── Edit Mode (user fill & export) ───────────────────────────────────────────
function EditMode({template}){
  const canvasRef=useRef(null);
  const [form,    setForm]    = useState({});
  const [uploads, setUploads] = useState({});
  const [busy,    setBusy]    = useState(false);

  useEffect(()=>{ if(canvasRef.current) drawCard(canvasRef.current,template,form,uploads,1); },[form,uploads,template]);

  function handleUploaded(fid,url){ setUploads(p=>({...p,[fid]:url})); }

  function handleDateInput(fid,raw){
    let v=raw.replace(/[^0-9]/g,"");
    if(v.length>2) v=v.slice(0,2)+"."+v.slice(2);
    if(v.length>5) v=v.slice(0,5)+"."+v.slice(5);
    if(v.length>10) v=v.slice(0,10);
    setForm(p=>({...p,[fid]:v}));
  }

  async function exportPNG(){
    setBusy(true);
    const off=document.createElement("canvas");
    await drawCard(off,template,form,uploads,2);
    off.toBlob(blob=>{
      const a=document.createElement("a"); a.href=URL.createObjectURL(blob);
      a.download=`CNIC_${template.name.replace(/\s+/g,"_")}.png`; a.click(); setBusy(false);
    },"image/png");
  }
  async function exportPDF(){
    setBusy(true);
    if(!window.jspdf) await new Promise((res,rej)=>{const s=document.createElement("script");s.src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";s.onload=res;s.onerror=rej;document.head.appendChild(s);});
    const off=document.createElement("canvas"); await drawCard(off,template,form,uploads,3);
    const {jsPDF}=window.jspdf;
    const pdf=new jsPDF({orientation:"landscape",unit:"mm",format:[85.6,54]});
    pdf.addImage(off.toDataURL("image/png"),"PNG",0,0,85.6,54);
    pdf.save(`CNIC_${template.name.replace(/\s+/g,"_")}.pdf`); setBusy(false);
  }

  const textFields  = template.fields.filter(f=>f.type==="text"||f.type==="watermark");
  const imageFields = template.fields.filter(f=>f.type==="image");
  const hasQR       = template.fields.some(f=>f.type==="qr");
  const isFront     = template.id==="front";
  const ORDER_FRONT = ["name","nameUrdu","fatherName","fatherNameUrdu","gender","country","idNumber","dob","issueDate","expiryDate","watermarkNum"];
  const ORDER_BACK  = ["cnicAboveQr","tempAddressUrdu","permAddressUrdu","familyCode"];
  const ordered     = (isFront?ORDER_FRONT:ORDER_BACK).map(id=>textFields.find(f=>f.id===id)).filter(Boolean);
  const isDate      = id=>id==="dob"||id.toLowerCase().includes("date");

  return (
    <div style={{display:"flex",gap:20,alignItems:"flex-start",flexWrap:"wrap"}}>
      <div style={{flex:"0 0 290px",minWidth:250}}>
        <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"15px 13px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:12}}>
            {isFront?"Front Card Info":"Back Card Info"}
          </div>
          {ordered.map(f=>(
            <label key={f.id} style={{display:"block",marginBottom:11}}>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.45)",display:"block",marginBottom:3}}>{FIELD_LABELS[f.id]||f.label}</span>
              <input
                type="text"
                dir={f.rtl?"rtl":"ltr"}
                value={form[f.id]||""}
                onChange={e=>isDate(f.id)?handleDateInput(f.id,e.target.value):setForm(p=>({...p,[f.id]:e.target.value}))}
                placeholder={isDate(f.id)?"DD.MM.YYYY":(FIELD_LABELS[f.id]||f.label)}
                maxLength={isDate(f.id)?10:undefined}
                style={{width:"100%",background:"rgba(0,0,0,0.35)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,color:"#fff",padding:"7px 10px",fontSize:f.rtl?16:13,boxSizing:"border-box",textAlign:f.rtl?"right":"left",direction:f.rtl?"rtl":"ltr",fontFamily:f.rtl?URDU_FONT:"inherit",lineHeight:f.rtl?2:1.4,fontVariantNumeric:"tabular-nums"}} />
            </label>
          ))}
          {hasQR&&(
            <label style={{display:"block",marginBottom:11}}>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.45)",display:"block",marginBottom:3}}>QR Code Data (blank = CNIC number)</span>
              <input type="text" value={form.qrData||""} onChange={e=>setForm(p=>({...p,qrData:e.target.value}))}
                placeholder="Leave blank to use CNIC number"
                style={{width:"100%",background:"rgba(0,0,0,0.35)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,color:"#fff",padding:"7px 10px",fontSize:13,boxSizing:"border-box"}} />
            </label>
          )}
          {imageFields.length>0&&(
            <div style={{borderTop:"1px solid rgba(255,255,255,0.07)",paddingTop:12,marginTop:4}}>
              <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:8}}>Photos</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginBottom:10,lineHeight:1.6,background:"rgba(99,102,241,0.06)",borderRadius:6,padding:"6px 8px"}}>
                📸 Upload photos already without background (transparent PNG)
              </div>
              {imageFields.map(f=>(
                <PhotoUploader key={f.id} fieldId={f.id} label={FIELD_LABELS[f.id]||f.label} onUploaded={handleUploaded} />
              ))}
            </div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:7,marginTop:10}}>
            <button onClick={exportPNG} disabled={busy}
              style={{padding:"10px",borderRadius:8,cursor:"pointer",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",border:"none",color:"#fff",fontWeight:700,fontSize:13,opacity:busy?0.6:1}}>
              {busy?"Exporting…":"⬇ Export PNG (2× High Res)"}
            </button>
            <button onClick={exportPDF} disabled={busy}
              style={{padding:"10px",borderRadius:8,cursor:"pointer",background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.13)",color:"rgba(255,255,255,0.7)",fontSize:13}}>
              {busy?"Exporting…":"⬇ Export PDF (85.6×54mm)"}
            </button>
          </div>
        </div>
      </div>
      <div style={{flex:"1 1 420px"}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:7,fontWeight:700}}>Live Preview</div>
        <div style={{background:"#080808",borderRadius:14,padding:10,boxShadow:"0 20px 60px rgba(0,0,0,0.7)"}}>
          <canvas ref={canvasRef} style={{width:"100%",display:"block",borderRadius:8}} />
        </div>
        <div style={{marginTop:7,fontSize:11,color:"rgba(255,255,255,0.2)",textAlign:"center"}}>Preview matches export exactly</div>
      </div>
    </div>
  );
}

// ─── Admin Login ──────────────────────────────────────────────────────────────
function AdminLogin({onLogin,onBack}){
  const [pw,setPw]=useState(""); const [err,setErr]=useState("");
  function attempt(){ if(pw===ADMIN_PASSWORD){onLogin();}else{setErr("Wrong password");setTimeout(()=>setErr(""),2000);} }
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#0f0c29,#302b63,#24243e)"}}>
      <div style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(99,102,241,0.4)",borderRadius:16,padding:36,width:340,textAlign:"center"}}>
        <div style={{fontSize:36,marginBottom:12}}>🔐</div>
        <h2 style={{margin:"0 0 6px",color:"#a78bfa",fontSize:20}}>Admin Panel</h2>
        <p style={{margin:"0 0 24px",fontSize:12,color:"rgba(255,255,255,0.35)"}}>Password required</p>
        <input type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&attempt()}
          placeholder="Enter admin password" autoFocus
          style={{width:"100%",background:"rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,color:"#fff",padding:"11px 14px",fontSize:14,boxSizing:"border-box",marginBottom:12,textAlign:"center"}} />
        {err&&<div style={{color:"#f87171",fontSize:12,marginBottom:10}}>{err}</div>}
        <button onClick={attempt}
          style={{width:"100%",padding:"11px",borderRadius:8,cursor:"pointer",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",border:"none",color:"#fff",fontWeight:700,fontSize:14,marginBottom:10}}>
          Login
        </button>
        <button onClick={onBack}
          style={{width:"100%",padding:"8px",borderRadius:8,cursor:"pointer",background:"transparent",border:"1px solid rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.4)",fontSize:12}}>
          ← Back to User View
        </button>
      </div>
    </div>
  );
}

// ─── Admin Panel ──────────────────────────────────────────────────────────────
function AdminPanel({onExit}){
  const [templates, setTemplates] = useState([
    {...DEFAULT_FRONT, fields:DEFAULT_FRONT.fields.map(f=>({...f}))},
    {...DEFAULT_BACK,  fields:DEFAULT_BACK.fields.map(f=>({...f}))}
  ]);
  const [activeId,  setActiveId]  = useState("front");
  const [pubStatus, setPubStatus] = useState("idle"); // idle | saving | done | error
  const [pubMsg,    setPubMsg]    = useState("");
  const [loaded,    setLoaded]    = useState(false);
  const active = templates.find(t=>t.id===activeId)||templates[0];

  // Load existing config from server on mount
  useEffect(()=>{
    fetchServerConfig().then(cfg=>{
      if(cfg&&cfg.templates&&cfg.templates.length>0){
        setTemplates(cfg.templates);
      }
      setLoaded(true);
    });
  },[]);

  function saveTemplate(updated){
    setTemplates(prev=>{
      // Always keep both templates — only replace the one that was saved
      const exists = prev.find(t=>t.id===updated.id);
      if(exists){
        return prev.map(t=>t.id===updated.id ? updated : t);
      } else {
        return [...prev, updated];
      }
    });
    setActiveId(updated.id);
  }

  // THE KEY FIX: Publish saves a JSON file to the server via a special endpoint
  // This file is then served to ALL users when they load the site
  async function publishToUsers(){
    setPubStatus("saving"); setPubMsg("");
    try{
      const config={ templates, publishedAt: new Date().toISOString() };
      const res = await fetch("/api/save-config",{
        method:"POST",
        headers:{"Content-Type":"application/json","X-Admin-Key": ADMIN_PASSWORD},
        body: JSON.stringify(config)
      });
      if(res.ok){
        setPubStatus("done"); setPubMsg("✅ Published! All users now see your template.");
      } else {
        const txt = await res.text();
        setPubStatus("error"); setPubMsg("❌ Error: "+txt);
      }
    }catch(e){
      setPubStatus("error"); setPubMsg("❌ Network error: "+e.message);
    }
    setTimeout(()=>setPubStatus("idle"),5000);
  }

  if(!loaded) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#0f0c29,#302b63,#24243e)",color:"#fff",fontSize:16}}>
      Loading current config...
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#0f0c29,#1a0a3e,#24243e)",fontFamily:"Arial,sans-serif",color:"#fff",padding:18,boxSizing:"border-box"}}>
      <div style={{maxWidth:1300,margin:"0 auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
          <div>
            <h1 style={{margin:0,fontSize:22,fontWeight:800,background:"linear-gradient(90deg,#f59e0b,#ef4444)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>
              🔐 Admin — CNIC Template Manager
            </h1>
            <p style={{margin:"3px 0 0",fontSize:12,color:"rgba(255,255,255,0.35)"}}>
              Set up Front & Back templates → Save each → Publish → done
            </p>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            {pubMsg&&(
              <div style={{padding:"8px 14px",borderRadius:8,background:pubStatus==="done"?"rgba(16,185,129,0.2)":"rgba(239,68,68,0.2)",border:`1px solid ${pubStatus==="done"?"#16a34a":"#dc2626"}`,color:pubStatus==="done"?"#6ee7b7":"#f87171",fontSize:13,fontWeight:600}}>
                {pubMsg}
              </div>
            )}
            <button onClick={publishToUsers} disabled={pubStatus==="saving"}
              style={{padding:"10px 22px",borderRadius:8,cursor:pubStatus==="saving"?"not-allowed":"pointer",background:pubStatus==="saving"?"rgba(245,158,11,0.3)":"linear-gradient(135deg,#f59e0b,#ef4444)",border:"none",color:"#fff",fontWeight:700,fontSize:14,opacity:pubStatus==="saving"?0.7:1}}>
              {pubStatus==="saving"?"⏳ Publishing…":"🚀 Publish to All Users"}
            </button>
            <button onClick={onExit}
              style={{padding:"10px 16px",borderRadius:8,cursor:"pointer",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.5)",fontSize:13}}>
              ← Exit Admin
            </button>
          </div>
        </div>

        <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.8}}>
          <strong style={{color:"#fcd34d"}}>How it works:</strong>{" "}
          1. Select <strong>Front</strong> → upload background → place & adjust fields → click <strong>💾 Save This Template</strong>{" "}
          2. Select <strong>Back</strong> → same steps → click <strong>💾 Save This Template</strong>{" "}
          3. Click <strong style={{color:"#f87171"}}>🚀 Publish to All Users</strong> — every user on every device sees your layout instantly.
        </div>

        <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:9,padding:"9px 13px",marginBottom:16,display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>Editing:</span>
          {templates.map(t=>(
            <button key={t.id} onClick={()=>setActiveId(t.id)}
              style={{padding:"5px 18px",borderRadius:14,cursor:"pointer",fontSize:13,background:activeId===t.id?"rgba(245,158,11,0.3)":"rgba(255,255,255,0.06)",border:`1px solid ${activeId===t.id?"rgba(245,158,11,0.6)":"rgba(255,255,255,0.1)"}`,color:activeId===t.id?"#fcd34d":"rgba(255,255,255,0.5)",fontWeight:activeId===t.id?700:400}}>
              {t.id==="front"?"🪪 Front Card":"🔄 Back Card"}
            </button>
          ))}
        </div>

        <SetupMode key={activeId+"_admin"} template={active} onSave={saveTemplate} />
      </div>
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
export default function App(){
  const [appMode,    setAppMode]    = useState("user");
  const [mode,       setMode]       = useState("edit");
  const [templates,  setTemplates]  = useState([DEFAULT_FRONT, DEFAULT_BACK]);
  const [activeId,   setActiveId]   = useState("front");
  const [loading,    setLoading]    = useState(true);
  const [hasConfig,  setHasConfig]  = useState(false);
  const active = templates.find(t=>t.id===activeId)||templates[0];

  // Load server config on startup — works for ALL users on ALL devices
  useEffect(()=>{
    fetchServerConfig().then(cfg=>{
      if(cfg&&cfg.templates&&cfg.templates.length>0){
        setTemplates(cfg.templates);
        setHasConfig(true);
        setMode("edit"); // users go straight to fill mode
      }
      setLoading(false);
    });
  },[]);

  function exitAdmin(){ 
    // Reload config after admin publishes
    setLoading(true);
    fetchServerConfig().then(cfg=>{
      if(cfg&&cfg.templates&&cfg.templates.length>0){
        setTemplates(cfg.templates); setHasConfig(true); setMode("edit");
      }
      setLoading(false); setAppMode("user");
    });
  }

  if(loading) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#0f0c29,#302b63,#24243e)",color:"#fff"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:32,marginBottom:12}}>🪪</div>
        <div style={{fontSize:16,color:"rgba(255,255,255,0.7)"}}>Loading CNIC Editor…</div>
      </div>
    </div>
  );
  if(appMode==="adminLogin") return <AdminLogin onLogin={()=>setAppMode("admin")} onBack={()=>setAppMode("user")} />;
  if(appMode==="admin")      return <AdminPanel onExit={exitAdmin} />;

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#0f0c29 0%,#302b63 50%,#24243e 100%)",fontFamily:"Arial,sans-serif",color:"#fff",padding:18,boxSizing:"border-box"}}>
      <div style={{maxWidth:1300,margin:"0 auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
          <div>
            <h1 style={{margin:0,fontSize:22,fontWeight:800,background:"linear-gradient(90deg,#a78bfa,#38bdf8)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>
              Pakistan CNIC Editor
            </h1>
            <p style={{margin:"2px 0 0",fontSize:11,color:"rgba(255,255,255,0.3)"}}>
              {hasConfig?"✅ Template ready — fill your data and export below":"Front + Back · QR Code · Urdu Addresses · Export PNG & PDF"}
            </p>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {!hasConfig&&(
              <div style={{background:"rgba(0,0,0,0.3)",borderRadius:9,padding:3,display:"flex",border:"1px solid rgba(255,255,255,0.08)"}}>
                {[["setup","⚙ Setup"],["edit","✏ Fill & Export"]].map(([m,lbl])=>(
                  <button key={m} onClick={()=>setMode(m)}
                    style={{padding:"7px 15px",borderRadius:7,cursor:"pointer",background:mode===m?"linear-gradient(135deg,#6366f1,#8b5cf6)":"transparent",border:"none",color:mode===m?"#fff":"rgba(255,255,255,0.4)",fontWeight:mode===m?700:400,fontSize:13}}>
                    {lbl}
                  </button>
                ))}
              </div>
            )}
            {/* Tiny hidden admin button */}
            <button onClick={()=>setAppMode("adminLogin")}
              title="Admin" style={{padding:"5px 9px",borderRadius:6,cursor:"pointer",background:"transparent",border:"1px solid rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.1)",fontSize:10}}>
              ⚙
            </button>
          </div>
        </div>

        <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:9,padding:"9px 13px",marginBottom:16,display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>Card Side:</span>
          {templates.map(t=>(
            <button key={t.id} onClick={()=>setActiveId(t.id)}
              style={{padding:"4px 13px",borderRadius:14,cursor:"pointer",fontSize:12,background:activeId===t.id?"rgba(99,102,241,0.3)":"rgba(255,255,255,0.06)",border:`1px solid ${activeId===t.id?"rgba(99,102,241,0.5)":"rgba(255,255,255,0.1)"}`,color:activeId===t.id?"#a5b4fc":"rgba(255,255,255,0.5)",fontWeight:activeId===t.id?700:400}}>
              {t.id==="front"?"🪪 Front":"🔄 Back"}
            </button>
          ))}
        </div>

        {mode==="setup"&&!hasConfig
          ?<SetupMode key={activeId} template={active} onSave={t=>setTemplates(p=>p.map(x=>x.id===t.id?t:x))}/>
          :<EditMode  key={activeId+"_e"} template={active}/>}
      </div>
    </div>
  );
}
